import React, { useEffect, useRef, useState } from "react";
import GridLayout, { WidthProvider } from "react-grid-layout";
import {
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  Dropdown,
  Modal,
  Spinner,
  Alert,
  ListGroup,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { crearFAQ, actualizarFAQ, subirMediaFAQ } from "../../redux/faqSlice";
import {
  buscarDocumentos,
  limpiarResultados,
} from "../../redux/busquedaDocumentosSlice";
import { resolverUrlArchivo } from "../../utils/resolverUrlArchivo";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ReactGridLayout = WidthProvider(GridLayout);

// Tamaños por defecto (w en 12 columnas, h en filas de 30px).
const DEFAULTS = {
  heading: { props: { text: "Nuevo título" }, size: { w: 12, h: 1 } },
  text: { props: { text: "" }, size: { w: 12, h: 3 } },
  button: {
    props: { label: "Ver más", href: "", variant: "primary" },
    size: { w: 3, h: 1 },
  },
  image: { props: { src: "", alt: "" }, size: { w: 6, h: 6 } },
  document: {
    props: { documentoId: null, titulo: "", url: "" },
    size: { w: 4, h: 1 },
  },
};

const ETIQUETA_TIPO = {
  heading: "Título",
  text: "Texto",
  button: "Botón",
  image: "Imagen",
  document: "Documento",
};

const nuevoId = () =>
  `blk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * FAQCanvasEditor
 * @param {object|null} faq  FAQ a editar (con canvas_data) o null para crear
 * @param {function} onGuardado  callback tras guardar OK
 * @param {function} onCancelar
 */
export default function FAQCanvasEditor({ faq, onGuardado, onCancelar }) {
  const dispatch = useDispatch();
  const { saving } = useSelector((s) => s.faq);

  const [titulo, setTitulo] = useState("");
  const [slug, setSlug] = useState("");
  const [estadoActivo, setEstadoActivo] = useState(true);
  const [version, setVersion] = useState(null);
  const [blocks, setBlocks] = useState({});
  const [layout, setLayout] = useState([]);
  const [editandoId, setEditandoId] = useState(null); // bloque en el modal de edición

  // Carga inicial desde la FAQ (modo edición).
  useEffect(() => {
    if (!faq) return;
    setTitulo(faq.titulo || "");
    setSlug(faq.slug || "");
    setEstadoActivo(faq.estado_activo ?? true);
    setVersion(faq.version ?? null);

    let cd = faq.canvas_data;
    if (typeof cd === "string") {
      try {
        cd = JSON.parse(cd);
      } catch {
        cd = null;
      }
    }
    setBlocks(cd?.blocks && typeof cd.blocks === "object" ? cd.blocks : {});
    setLayout(
      Array.isArray(cd?.layout) ? cd.layout.filter((it) => it && it.i) : [],
    );
  }, [faq]);

  // ------- Bloques -------
  const agregarBloque = (tipo) => {
    const id = nuevoId();
    const def = DEFAULTS[tipo];
    setBlocks((b) => ({ ...b, [id]: { type: tipo, props: { ...def.props } } }));
    setLayout((l) => [
      ...l,
      { i: id, x: 0, y: Infinity, w: def.size.w, h: def.size.h },
    ]);
    setEditandoId(id); // abre el modal para editarlo enseguida
  };

  const eliminarBloque = (id) => {
    setBlocks((b) => {
      const copia = { ...b };
      delete copia[id];
      return copia;
    });
    setLayout((l) => l.filter((it) => it.i !== id));
  };

  const actualizarProps = (id, nuevasProps) => {
    setBlocks((b) => ({
      ...b,
      [id]: { ...b[id], props: { ...b[id].props, ...nuevasProps } },
    }));
  };

  // ------- Guardar -------
  const guardar = async () => {
    if (!titulo.trim()) {
      Swal.fire("Falta el título", "La FAQ necesita un título.", "warning");
      return;
    }
    const canvas_data = { schemaVersion: 1, layout, blocks };
    const payload = {
      titulo: titulo.trim(),
      estado_activo: estadoActivo,
      canvas_data,
    };
    if (slug.trim()) payload.slug = slug.trim();

    try {
      if (faq) {
        payload.version = version; // optimistic locking
        await dispatch(actualizarFAQ({ id: faq.id, ...payload })).unwrap();
      } else {
        await dispatch(crearFAQ(payload)).unwrap();
      }
      Swal.fire("Guardado", "La FAQ se guardó correctamente.", "success");
      onGuardado?.();
    } catch (err) {
      if (err?.status === 409) {
        Swal.fire(
          "Conflicto de edición",
          "Otro administrador modificó esta FAQ. Recarga y vuelve a intentar.",
          "warning",
        );
      } else if (err?.status === 422 && err?.errors?.length) {
        // Lista clara de qué corregir
        const detalle = err.errors
          .map((e) => `• ${e.campo}: ${e.mensaje}`)
          .join("<br>");
        Swal.fire({
          icon: "warning",
          title: "Revisa estos campos",
          html: detalle,
        });
      } else {
        Swal.fire(
          "Error",
          err?.message || "No se pudo guardar la FAQ.",
          "error",
        );
      }
    }
  };

  const bloqueEnEdicion = editandoId ? blocks[editandoId] : null;

  return (
    <div>
      {/* Cabecera del editor */}
      <Row className="g-2 align-items-end mb-3">
        <Col xs={12} md={5}>
          <Form.Label className="small fw-bold mb-1">
            Título de la FAQ *
          </Form.Label>
          <Form.Control
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ej: Seguro complementario"
          />
        </Col>
        <Col xs={12} md={4}>
          <Form.Label className="small fw-bold mb-1">
            Slug (opcional)
          </Form.Label>
          <Form.Text className="text-muted">
            Solo minúsculas, números y guiones. Déjalo vacío para generarlo del
            título.
          </Form.Text>
          <Form.Control
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="se genera del título si lo dejas vacío"
          />
        </Col>
        <Col xs={6} md={3} className="d-flex align-items-center gap-3 pt-3">
          <Form.Check
            type="switch"
            id="faq-activo"
            label="Activa"
            checked={estadoActivo}
            onChange={(e) => setEstadoActivo(e.target.checked)}
          />
        </Col>
      </Row>

      {/* Barra de herramientas */}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3 p-2 bg-light rounded border">
        <Dropdown>
          <Dropdown.Toggle variant="success" size="sm">
            <i className="bi bi-plus-lg me-1"></i> Agregar bloque
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {Object.keys(DEFAULTS).map((tipo) => (
              <Dropdown.Item key={tipo} onClick={() => agregarBloque(tipo)}>
                {ETIQUETA_TIPO[tipo]}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        <span className="text-muted small ms-2">
          Arrastra desde la barra de cada bloque para mover; tira de la esquina
          para redimensionar.
        </span>
        <div className="ms-auto d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={onCancelar}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={guardar}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner as="span" size="sm" animation="border" /> Guardando…
              </>
            ) : (
              <>
                <i className="bi bi-save me-1"></i> Guardar FAQ
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Lienzo */}
      {layout.length === 0 ? (
        <Alert variant="info">
          Agrega tu primer bloque con el botón «Agregar bloque».
        </Alert>
      ) : (
        <div className="border rounded bg-white" style={{ minHeight: 200 }}>
          <ReactGridLayout
            className="layout"
            layout={layout}
            onLayoutChange={(nuevo) => setLayout(nuevo)}
            cols={12}
            rowHeight={30}
            margin={[12, 12]}
            draggableHandle=".rgl-drag"
            draggableCancel=".rgl-no-drag"
            compactType="vertical"
          >
            {layout.map((it) => {
              const bloque = blocks[it.i];
              if (!bloque) return <div key={it.i} />;
              return (
                <div
                  key={it.i}
                  className="border rounded bg-light d-flex flex-column overflow-hidden"
                >
                  {/* Barra del bloque (mango de arrastre + acciones) */}
                  <div
                    className="rgl-drag d-flex justify-content-between align-items-center px-2 py-1 bg-secondary text-white small"
                    style={{ cursor: "move" }}
                  >
                    <span className="text-truncate">
                      {ETIQUETA_TIPO[bloque.type] || bloque.type}
                    </span>
                    <ButtonGroup size="sm" className="rgl-no-drag">
                      <Button
                        variant="light"
                        className="py-0 px-2 rgl-no-drag d-flex align-items-center gap-1"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => setEditandoId(it.i)}
                        title="Editar bloque"
                      >
                        <i className="bi bi-pencil"></i>
                        <span>Editar</span>
                      </Button>
                      <Button
                        variant="light"
                        className="py-0 px-2 rgl-no-drag d-flex align-items-center gap-1 text-danger"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => eliminarBloque(it.i)}
                        title="Eliminar bloque"
                      >
                        <i className="bi bi-trash"></i>
                        <span>Eliminar</span>
                      </Button>
                    </ButtonGroup>
                  </div>
                  {/* Vista previa del bloque */}
                  <div className="p-2 flex-grow-1 overflow-auto">
                    <PreviewBloque bloque={bloque} />
                  </div>
                </div>
              );
            })}
          </ReactGridLayout>
        </div>
      )}

      {/* Modal de edición de bloque */}
      <ModalEditarBloque
        show={!!editandoId}
        bloque={bloqueEnEdicion}
        onClose={() => setEditandoId(null)}
        onChangeProps={(np) => actualizarProps(editandoId, np)}
      />
    </div>
  );
}

// -------------------- Preview de bloque (dentro del editor) --------------------
function PreviewBloque({ bloque }) {
  const { type, props = {} } = bloque;
  switch (type) {
    case "heading":
      return (
        <h2 className="h5 fw-bold text-danger mb-0">
          {props.text || "(título vacío)"}
        </h2>
      );
    case "text":
      return (
        <div style={{ whiteSpace: "pre-wrap" }}>
          {props.text || <span className="text-muted">(texto vacío)</span>}
        </div>
      );
    case "button":
      return (
        <Button size="sm" variant={props.variant || "primary"} disabled>
          {props.label || "Botón"}
        </Button>
      );
    case "image":
      return props.src ? (
        <img
          src={resolverUrlArchivo(props.src)}
          alt={props.alt || ""}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <span className="text-muted small">Sin imagen — edita para subir</span>
      );
    case "document":
      return props.titulo ? (
        <span>
          <i className="bi bi-file-earmark-text me-1"></i>
          {props.titulo}
        </span>
      ) : (
        <span className="text-muted small">
          Sin documento — edita para elegir
        </span>
      );
    default:
      return null;
  }
}

// -------------------- Modal de edición de bloque --------------------
function ModalEditarBloque({ show, bloque, onClose, onChangeProps }) {
  if (!bloque) return null;
  const { type, props = {} } = bloque;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar {ETIQUETA_TIPO[type] || type}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {type === "heading" && (
          <Form.Group>
            <Form.Label>Texto del título</Form.Label>
            <Form.Control
              value={props.text || ""}
              onChange={(e) => onChangeProps({ text: e.target.value })}
            />
          </Form.Group>
        )}

        {type === "text" && (
          <Form.Group>
            <Form.Label>Texto</Form.Label>
            <Form.Text className="text-muted">
              Formato: <code>**negrita**</code>, <code>*cursiva*</code>, y
              enlaces con <code>[texto](https://url.com)</code>. Los saltos de
              línea se respetan.
            </Form.Text>
            <Form.Control
              as="textarea"
              rows={5}
              value={props.text || ""}
              onChange={(e) => onChangeProps({ text: e.target.value })}
            />
          </Form.Group>
        )}

        {type === "button" && (
          <>
            <Form.Group className="mb-2">
              <Form.Label>Etiqueta</Form.Label>
              <Form.Control
                value={props.label || ""}
                onChange={(e) => onChangeProps({ label: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Enlace (URL)</Form.Label>
              <Form.Control
                value={props.href || ""}
                onChange={(e) => onChangeProps({ href: e.target.value })}
                placeholder="https://…"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Estilo</Form.Label>
              <Form.Select
                value={props.variant || "primary"}
                onChange={(e) => onChangeProps({ variant: e.target.value })}
              >
                <option value="primary">Primario</option>
                <option value="success">Éxito</option>
                <option value="danger">Peligro</option>
                <option value="outline-primary">Contorno</option>
              </Form.Select>
            </Form.Group>
          </>
        )}

        {type === "image" && (
          <EditorImagen props={props} onChangeProps={onChangeProps} />
        )}

        {type === "document" && (
          <EditorDocumento props={props} onChangeProps={onChangeProps} />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Listo
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// -------------------- Editor de imagen (subida vía backend) --------------------
function EditorImagen({ props, onChangeProps }) {
  const dispatch = useDispatch();
  const [subiendo, setSubiendo] = useState(false);
  const [errorSubida, setErrorSubida] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    setErrorSubida(null);
    try {
      const data = await dispatch(subirMediaFAQ(file)).unwrap();
      onChangeProps({ src: data.url }); // url relativa (/public/faqs/…)
    } catch (err) {
      setErrorSubida(
        typeof err === "string" ? err : "No se pudo subir la imagen.",
      );
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Imagen (JPG/PNG/WebP, máx 5MB)</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={onFile}
          disabled={subiendo}
        />
      </Form.Group>
      {subiendo && (
        <div className="text-muted small">
          <Spinner size="sm" animation="border" /> Subiendo…
        </div>
      )}
      {errorSubida && (
        <Alert variant="danger" className="py-1 my-1">
          {errorSubida}
        </Alert>
      )}
      {props.src && (
        <div className="mt-2">
          <img
            src={resolverUrlArchivo(props.src)}
            alt={props.alt || ""}
            style={{ maxWidth: "100%", maxHeight: 160, objectFit: "contain" }}
          />
        </div>
      )}
      <Form.Group className="mt-2">
        <Form.Label>Texto alternativo (accesibilidad)</Form.Label>
        <Form.Control
          value={props.alt || ""}
          onChange={(e) => onChangeProps({ alt: e.target.value })}
        />
      </Form.Group>
    </>
  );
}

// -------------------- Editor de documento (búsqueda typeahead) --------------------
function EditorDocumento({ props, onChangeProps }) {
  const dispatch = useDispatch();
  const { resultados, cargando } = useSelector((s) => s.busquedaDocumentos);
  const [q, setQ] = useState("");
  const debounceRef = useRef(null);

  const onQuery = (valor) => {
    setQ(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (valor.trim().length < 2) {
      dispatch(limpiarResultados());
      return;
    }
    debounceRef.current = setTimeout(
      () => dispatch(buscarDocumentos(valor.trim())),
      300,
    );
  };

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const elegir = (doc) => {
    onChangeProps({ documentoId: doc.id, titulo: doc.titulo, url: doc.url });
    setQ("");
    dispatch(limpiarResultados());
  };

  return (
    <>
      {props.titulo && (
        <Alert variant="success" className="py-2">
          <i className="bi bi-check-circle me-1"></i> Enlazado:{" "}
          <strong>{props.titulo}</strong>
        </Alert>
      )}
      <Form.Group>
        <Form.Label>Buscar documento</Form.Label>
        <Form.Control
          value={q}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Escribe al menos 2 caracteres…"
        />
      </Form.Group>
      {cargando && (
        <div className="text-muted small mt-2">
          <Spinner size="sm" animation="border" /> Buscando…
        </div>
      )}
      {resultados.length > 0 && (
        <ListGroup
          className="mt-2"
          style={{ maxHeight: 200, overflowY: "auto" }}
        >
          {resultados.map((doc) => (
            <ListGroup.Item
              action
              key={doc.id}
              onClick={() => elegir(doc)}
              className="text-truncate"
              title={doc.titulo}
            >
              <i className="bi bi-file-earmark-text me-2"></i>
              {doc.titulo}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </>
  );
}
